/**
 * Export Dialog — modal for exporting event data and map screenshots.
 *
 * Formats:
 *   - JSON: full event data
 *   - CSV: tabular event data
 *   - PNG: map canvas screenshot (composites MapLibre + deck.gl canvases)
 *
 * On desktop (Tauri): uses native save dialog when available.
 * On web: uses URL.createObjectURL() + download anchor.
 */

import { appStore } from '@/core/store';
import { isTauriRuntime } from '@/utils/tauri';
import type { NormalizedEvent } from '@/sources/base-source';

// ---------------------------------------------------------------------------
// CSV / JSON serialization (shared utilities)
// ---------------------------------------------------------------------------

/** Escape a field for RFC 4180 CSV. */
export function escapeCSVField(value: unknown): string {
  const str = String(value ?? '');
  if (str.includes('"') || str.includes(',') || str.includes('\n') || str.includes('\r')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

/** Serialize events to CSV string. */
export function eventsToCSV(events: NormalizedEvent[]): string {
  const headers = ['id', 'source', 'timestamp', 'type', 'severity', 'title', 'lat', 'lng', 'description'];
  const rows = events.map((e) => [
    escapeCSVField(e.id),
    escapeCSVField(e.source),
    escapeCSVField(e.timestamp),
    escapeCSVField(e.type),
    escapeCSVField(e.severity ?? ''),
    escapeCSVField(e.title ?? ''),
    escapeCSVField(e.location?.lat ?? ''),
    escapeCSVField(e.location?.lng ?? ''),
    escapeCSVField(e.description ?? ''),
  ].join(','));
  return [headers.join(','), ...rows].join('\n');
}

/** Serialize events to pretty-printed JSON string. */
export function eventsToJSON(events: NormalizedEvent[]): string {
  return JSON.stringify(events, null, 2);
}

// ---------------------------------------------------------------------------
// Map screenshot capture
// ---------------------------------------------------------------------------

/**
 * Capture the current map view as a PNG Blob.
 * Composites the MapLibre canvas and the deck.gl overlay canvas.
 */
export async function captureMapPNG(): Promise<Blob> {
  const mapContainer = document.getElementById('map-container');
  if (!mapContainer) throw new Error('Map container not found');

  // Find all canvas elements within the map container
  const canvases = mapContainer.querySelectorAll('canvas');
  if (canvases.length === 0) throw new Error('No map canvases found');

  // Use the first canvas dimensions as reference
  const refCanvas = canvases[0] as HTMLCanvasElement;
  const width = refCanvas.width;
  const height = refCanvas.height;

  // Create composite canvas
  const composite = document.createElement('canvas');
  composite.width = width;
  composite.height = height;
  const ctx = composite.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context unavailable');

  // Draw each canvas layer in DOM order (MapLibre base, then deck.gl overlay)
  for (const canvas of canvases) {
    try {
      ctx.drawImage(canvas as HTMLCanvasElement, 0, 0, width, height);
    } catch {
      // Cross-origin canvas — skip silently
    }
  }

  return new Promise<Blob>((resolve, reject) => {
    composite.toBlob(
      (blob) => blob ? resolve(blob) : reject(new Error('Canvas toBlob failed')),
      'image/png',
    );
  });
}

// ---------------------------------------------------------------------------
// File download helper
// ---------------------------------------------------------------------------

const IS_DESKTOP = isTauriRuntime();

/** Trigger a file download via web API (createObjectURL). */
function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    // Revoke after a short delay to ensure download starts
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}

/**
 * Save a blob to disk. On desktop, tries the native Tauri save dialog.
 * Falls back to web download on failure or when not in Tauri.
 */
async function saveFile(blob: Blob, defaultFilename: string): Promise<void> {
  if (IS_DESKTOP) {
    try {
      // Dynamic import with variable path to bypass TS module resolution —
      // these plugins may not be in node_modules during web-only development.
      const dialogPkg = '@tauri-apps/plugin-dialog';
      const dialogMod = await import(/* @vite-ignore */ dialogPkg);
      const path: string | null = await dialogMod.save({
        defaultPath: defaultFilename,
        filters: [{ name: 'All Files', extensions: ['*'] }],
      });
      if (path) {
        const fsPkg = '@tauri-apps/plugin-fs';
        const fsMod = await import(/* @vite-ignore */ fsPkg);
        const buffer = await blob.arrayBuffer();
        await fsMod.writeFile(path, new Uint8Array(buffer));
        return;
      }
      // User cancelled — do nothing
      return;
    } catch {
      // Plugin not available — fall through to web download
    }
  }

  downloadBlob(blob, defaultFilename);
}

// ---------------------------------------------------------------------------
// Export Dialog UI
// ---------------------------------------------------------------------------

export type ExportFormat = 'json' | 'csv' | 'png';

interface ExportOption {
  format: ExportFormat;
  label: string;
  description: string;
  icon: string;
}

const EXPORT_OPTIONS: ExportOption[] = [
  { format: 'json', label: 'JSON', description: 'Full event data with all fields', icon: '{ }' },
  { format: 'csv', label: 'CSV', description: 'Tabular data for spreadsheets', icon: '\u2261' },
  { format: 'png', label: 'PNG', description: 'Map screenshot (current view)', icon: '\u{1F5BC}' },
];

/** Show the export dialog and return the user's chosen export. */
export function showExportDialog(): void {
  // Remove any existing dialog
  const existing = document.getElementById('sentinel-export-dialog');
  if (existing) existing.remove();

  const overlay = document.createElement('div');
  overlay.id = 'sentinel-export-dialog';
  overlay.style.cssText = `
    position: fixed; inset: 0; z-index: 10000;
    display: flex; align-items: center; justify-content: center;
    background: rgba(0, 0, 0, 0.6); backdrop-filter: blur(2px);
  `;

  const dialog = document.createElement('div');
  dialog.style.cssText = `
    background: var(--bg-primary, #0d0d0d);
    border: 1px solid var(--border-primary, #2a2a2a);
    border-radius: 12px; padding: 20px; width: 360px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
    font-family: inherit;
  `;

  // Header
  const header = document.createElement('div');
  header.style.cssText = 'display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px;';

  const title = document.createElement('h3');
  title.style.cssText = 'margin: 0; font-size: 14px; font-weight: 600; color: var(--text-primary, #e0e0e0);';
  title.textContent = 'Export Data';
  header.appendChild(title);

  const closeBtn = document.createElement('button');
  closeBtn.style.cssText = `
    background: none; border: none; color: var(--text-muted, #666);
    font-size: 18px; cursor: pointer; padding: 0 4px; line-height: 1;
  `;
  closeBtn.textContent = '\u2715';
  closeBtn.onclick = () => overlay.remove();
  header.appendChild(closeBtn);

  dialog.appendChild(header);

  // Event count info
  const eventCount = appStore.getState().events.length;
  const info = document.createElement('div');
  info.style.cssText = 'font-size: 11px; color: var(--text-muted, #666); margin-bottom: 12px;';
  info.textContent = `${eventCount} events loaded`;
  dialog.appendChild(info);

  // Status area
  const statusArea = document.createElement('div');
  statusArea.style.cssText = 'font-size: 11px; margin-top: 12px; min-height: 20px;';

  // Format buttons
  const btnGrid = document.createElement('div');
  btnGrid.style.cssText = 'display: flex; flex-direction: column; gap: 8px;';

  for (const opt of EXPORT_OPTIONS) {
    const btn = document.createElement('button');
    btn.style.cssText = `
      display: flex; align-items: center; gap: 12px;
      padding: 12px 14px; border-radius: 8px; cursor: pointer;
      background: var(--bg-secondary, #161616);
      border: 1px solid var(--border-primary, #2a2a2a);
      color: var(--text-primary, #e0e0e0);
      text-align: left; font-family: inherit; font-size: 13px;
      transition: border-color 0.15s;
    `;
    btn.onmouseenter = () => { btn.style.borderColor = 'var(--accent, #4fc3f7)'; };
    btn.onmouseleave = () => { btn.style.borderColor = 'var(--border-primary, #2a2a2a)'; };

    const icon = document.createElement('span');
    icon.style.cssText = 'font-size: 16px; width: 24px; text-align: center; flex-shrink: 0;';
    icon.textContent = opt.icon;
    btn.appendChild(icon);

    const text = document.createElement('div');
    text.style.cssText = 'flex: 1;';
    const label = document.createElement('div');
    label.style.cssText = 'font-weight: 600; font-size: 13px;';
    label.textContent = opt.label;
    text.appendChild(label);
    const desc = document.createElement('div');
    desc.style.cssText = 'font-size: 11px; color: var(--text-muted, #666); margin-top: 2px;';
    desc.textContent = opt.description;
    text.appendChild(desc);
    btn.appendChild(text);

    btn.onclick = () => handleExport(opt.format, statusArea);
    btnGrid.appendChild(btn);
  }

  dialog.appendChild(btnGrid);
  dialog.appendChild(statusArea);
  overlay.appendChild(dialog);

  function closeDialog(): void {
    overlay.remove();
    document.removeEventListener('keydown', onKey);
  }

  // Close on overlay click
  overlay.onclick = (e) => {
    if (e.target === overlay) closeDialog();
  };

  // Close on Escape
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') closeDialog();
  };
  document.addEventListener('keydown', onKey);

  // Also update close button to use shared cleanup
  closeBtn.onclick = () => closeDialog();

  document.body.appendChild(overlay);
}

/** Handle the actual export for a given format. */
async function handleExport(format: ExportFormat, statusEl: HTMLElement): Promise<void> {
  const events = appStore.getState().events;
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);

  statusEl.style.color = 'var(--text-secondary, #999)';
  statusEl.textContent = 'Exporting...';

  try {
    let blob: Blob;
    let filename: string;

    switch (format) {
      case 'json': {
        if (events.length === 0) throw new Error('No events to export');
        blob = new Blob([eventsToJSON(events)], { type: 'application/json' });
        filename = `sentinel-events-${timestamp}.json`;
        break;
      }
      case 'csv': {
        if (events.length === 0) throw new Error('No events to export');
        blob = new Blob([eventsToCSV(events)], { type: 'text/csv' });
        filename = `sentinel-events-${timestamp}.csv`;
        break;
      }
      case 'png': {
        blob = await captureMapPNG();
        filename = `sentinel-map-${timestamp}.png`;
        break;
      }
    }

    await saveFile(blob, filename);

    statusEl.style.color = 'var(--accent-green, #66bb6a)';
    statusEl.textContent = `Exported ${filename}`;

    // Auto-close after success
    setTimeout(() => {
      const overlay = document.getElementById('sentinel-export-dialog');
      if (overlay) overlay.remove();
    }, 1500);
  } catch (err) {
    statusEl.style.color = 'var(--accent-red, #ef5350)';
    statusEl.textContent = err instanceof Error ? err.message : 'Export failed';
  }
}
